import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner, Alert, Form, Row, Col } from 'react-bootstrap';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer
} from 'recharts';
import { getTempHistory } from '../services/api';

// Compute dew point from temperature (°F) and humidity (%)
function dewPoint(tempF, humidity) {
  const tempC = (tempF - 32) * 5 / 9;
  const a = 17.27;
  const b = 237.7;
  const alpha = ((a * tempC) / (b + tempC)) + Math.log(humidity / 100);
  const dpC = (b * alpha) / (a - alpha);
  return Math.round(((dpC * 9 / 5) + 32) * 100) / 100;
}

const RANGE_OPTIONS = [
  { label: '24h', value: '24h' },
  { label: '3d', value: '3d' },
  { label: '7d', value: '7d' },
];

function formatTimestamp(ts) {
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: false
  });
}

function formatXTick(ts) {
  const d = new Date(ts);
  return `${(d.getMonth() + 1)}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const CustomTooltip = ({ active, payload, label, tempLow, tempHigh, humLow, humHigh }) => {
  if (!active || !payload || !payload.length) return null;
  const d = new Date(label);
  const timeStr = d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div style={{
      background: 'rgba(255,255,255,0.97)', border: '1px solid #ccc',
      borderRadius: 6, padding: '8px 12px', fontSize: 12, minWidth: 160
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{timeStr}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span>{p.name}:</span>
          <span style={{ fontWeight: 600 }}>
            {p.value != null ? p.value.toFixed(1) : '—'}
            {p.dataKey === 'humidity' ? '%' : '°F'}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function TempHistoryModal({ show, onHide, idLevels, lotId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('24h');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [usingCustom, setUsingCustom] = useState(false);

  const fetchData = useCallback(async () => {
    if (!idLevels) return;
    setLoading(true);
    setError(null);
    try {
      const params = usingCustom && startDate && endDate
        ? { startDate, endDate }
        : { range };
      const result = await getTempHistory(idLevels, params);
      setData(result);
    } catch (e) {
      setError('Failed to load temperature history.');
    } finally {
      setLoading(false);
    }
  }, [idLevels, range, startDate, endDate, usingCustom]);

  useEffect(() => {
    if (show) fetchData();
  }, [show, fetchData]);

  const handleRangeClick = (r) => {
    setRange(r);
    setUsingCustom(false);
  };

  const handleCustomApply = () => {
    if (startDate && endDate) {
      setUsingCustom(true);
    }
  };

  // Prepare chart data with dew point
  const chartData = (data?.logs || []).map(row => ({
    ts: new Date(row.timestamp).getTime(),
    temperature: row.temperatureF != null ? Number(row.temperatureF) : null,
    humidity: row.humidity != null ? Number(row.humidity) : null,
    dewPoint: (row.temperatureF != null && row.humidity != null)
      ? dewPoint(Number(row.temperatureF), Number(row.humidity))
      : null,
    isAlert: row.isAlert
  }));

  const tempLow = data?.tempLow != null ? Number(data.tempLow) : null;
  const tempHigh = data?.tempHigh != null ? Number(data.tempHigh) : null;
  const humLow = data?.humLow != null ? Number(data.humLow) : null;
  const humHigh = data?.humHigh != null ? Number(data.humHigh) : null;

  // Y axis domain for temperature — pad around data and tolerances
  const allTemps = chartData.map(d => d.temperature).filter(v => v != null);
  const allDp = chartData.map(d => d.dewPoint).filter(v => v != null);
  const tempYMin = Math.floor(Math.min(...allTemps, ...allDp, tempLow ?? Infinity) - 5);
  const tempYMax = Math.ceil(Math.max(...allTemps, tempHigh ?? -Infinity) + 5);
  const humYMin = 0;
  const humYMax = 100;

  // Tick formatter — reduce density
  const tickCount = 8;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered dialogClassName="temp-history-modal">
      <Modal.Header closeButton style={{ background: '#f0f4ff', borderBottom: '1px solid #d0d9ee' }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          Temperature History — {lotId}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '16px 20px' }}>
        {/* Tolerance summary */}
        {data && (
          <div style={{ fontSize: 12, marginBottom: 10, color: '#444' }}>
            {tempLow != null && tempHigh != null && (
              <span style={{ marginRight: 24 }}>
                Temperature Range: <strong>{tempLow.toFixed(2)}°F – {tempHigh.toFixed(2)}°F</strong>
              </span>
            )}
            {humLow != null && humHigh != null && (
              <span>
                Humidity Range: <strong>{humLow.toFixed(2)}% – {humHigh.toFixed(2)}%</strong>
              </span>
            )}
          </div>
        )}

        {/* Controls */}
        <Row className="align-items-end mb-3 g-2">
          <Col xs="auto">
            <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>Start Date</Form.Label>
            <Form.Control
              type="datetime-local"
              size="sm"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ fontSize: 12 }}
            />
          </Col>
          <Col xs="auto">
            <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>End Date</Form.Label>
            <Form.Control
              type="datetime-local"
              size="sm"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={{ fontSize: 12 }}
            />
          </Col>
          <Col xs="auto" style={{ paddingTop: 20 }}>
            <Button size="sm" variant="outline-secondary" onClick={handleCustomApply}
              disabled={!startDate || !endDate} style={{ fontSize: 12 }}>
              Apply
            </Button>
          </Col>
          <Col />
          <Col xs="auto" style={{ paddingTop: 20 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {RANGE_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  size="sm"
                  variant={!usingCustom && range === opt.value ? 'primary' : 'outline-secondary'}
                  onClick={() => handleRangeClick(opt.value)}
                  style={{ fontSize: 12, minWidth: 36 }}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </Col>
        </Row>

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        )}

        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {!loading && !error && chartData.length === 0 && (
          <Alert variant="info" className="py-2">No temperature data found for this period.</Alert>
        )}

        {!loading && chartData.length > 0 && (
          <>
            {/* Temperature Chart */}
            <div style={{ marginBottom: 8 }}>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 10, right: 60, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical />

                  {/* Normal range shaded band */}
                  {tempLow != null && tempHigh != null && (
                    <ReferenceArea
                      y1={tempLow} y2={tempHigh}
                      fill="#c8e6c9" fillOpacity={0.4}
                      strokeOpacity={0}
                    />
                  )}

                  <XAxis
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={formatXTick}
                    tick={{ fontSize: 10 }}
                    tickCount={tickCount}
                  />
                  <YAxis
                    domain={[isFinite(tempYMin) ? tempYMin : 'auto', isFinite(tempYMax) ? tempYMax : 'auto']}
                    tick={{ fontSize: 10 }}
                    label={{ value: 'Temperature (°F)', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11 } }}
                  />
                  <Tooltip content={<CustomTooltip tempLow={tempLow} tempHigh={tempHigh} />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />

                  {tempLow != null && (
                    <ReferenceLine y={tempLow} stroke="#2196f3" strokeDasharray="4 4" strokeWidth={1}
                      label={{ value: `${tempLow}°F`, position: 'right', fontSize: 10, fill: '#2196f3' }} />
                  )}
                  {tempHigh != null && (
                    <ReferenceLine y={tempHigh} stroke="#f44336" strokeDasharray="4 4" strokeWidth={1}
                      label={{ value: `${tempHigh}°F`, position: 'right', fontSize: 10, fill: '#f44336' }} />
                  )}

                  <Line
                    type="monotone"
                    dataKey="temperature"
                    name="Temperature (°F)"
                    stroke="#e53935"
                    dot={false}
                    strokeWidth={1.5}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="dewPoint"
                    name="Dew Point (°F)"
                    stroke="#8b00ff"
                    dot={false}
                    strokeWidth={1.5}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Humidity Chart */}
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 5, right: 60, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                {humLow != null && humHigh != null && (
                  <ReferenceArea
                    y1={humLow} y2={humHigh}
                    fill="#c8e6c9" fillOpacity={0.4}
                    strokeOpacity={0}
                  />
                )}

                <XAxis
                  dataKey="ts"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={formatXTick}
                  tick={{ fontSize: 10 }}
                  tickCount={tickCount}
                  label={{ value: 'Time', position: 'insideBottom', offset: -2, style: { fontSize: 11 } }}
                />
                <YAxis
                  domain={[humYMin, humYMax]}
                  tick={{ fontSize: 10 }}
                  label={{ value: 'Humidity (%)', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11 } }}
                />
                <Tooltip content={<CustomTooltip humLow={humLow} humHigh={humHigh} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />

                {humLow != null && (
                  <ReferenceLine y={humLow} stroke="#2196f3" strokeDasharray="4 4" strokeWidth={1}
                    label={{ value: `${humLow}%`, position: 'right', fontSize: 10, fill: '#2196f3' }} />
                )}
                {humHigh != null && (
                  <ReferenceLine y={humHigh} stroke="#f44336" strokeDasharray="4 4" strokeWidth={1}
                    label={{ value: `${humHigh}%`, position: 'right', fontSize: 10, fill: '#f44336' }} />
                )}

                <Line
                  type="monotone"
                  dataKey="humidity"
                  name="Humidity (%)"
                  stroke="#42a5f5"
                  dot={false}
                  strokeWidth={1.5}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={{ padding: '8px 16px' }}>
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
